import { useMemo } from "react";
import { CATEGORY_MAP } from "../utils/constants";
import ProfileAvatar from "./ProfileAvatar";
import styles from "../styles/theme";

export default function ProfileView({
  person,
  people,
  onEdit,
  onDelete,
  onSelectPerson,
}) {
  const cat = CATEGORY_MAP[person.category];
  const parent = people.find((p) => p.id === person.parentId);
  const children = people.filter((p) => p.parentId === person.id);
  const daysSinceMet = person.dateMet
    ? Math.floor((new Date() - new Date(person.dateMet)) / 86400000)
    : null;

  // Compute age from birthdate
  const age = useMemo(() => {
    if (!person.birthdate) return null;
    const b = new Date(person.birthdate + "T00:00:00");
    const now = new Date();
    let a = now.getFullYear() - b.getFullYear();
    if (
      now.getMonth() < b.getMonth() ||
      (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())
    )
      a--;
    return a;
  }, [person.birthdate]);

  // Next birthday
  const nextBirthday = useMemo(() => {
    if (!person.birthdate) return null;
    const b = new Date(person.birthdate + "T00:00:00");
    const now = new Date();
    const thisYear = new Date(now.getFullYear(), b.getMonth(), b.getDate());
    const next =
      thisYear >= now
        ? thisYear
        : new Date(now.getFullYear() + 1, b.getMonth(), b.getDate());
    const days = Math.ceil((next - now) / 86400000);
    return days === 0
      ? "Today! 🎉"
      : days === 1
        ? "Tomorrow!"
        : `In ${days} days`;
  }, [person.birthdate]);

  const formatDate = (d) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  return (
    <div style={styles.profileContainer}>
      {/* Hero Header */}
      <div
        style={{
          ...styles.profileHero,
          borderBottomColor: cat?.color || "#333",
        }}
      >
        <ProfileAvatar
          person={person}
          size={88}
          fontSize={36}
          color={cat?.color}
        />
        <div style={styles.profileHeroInfo}>
          <h1 style={styles.profileName}>{person.name}</h1>
          <div style={styles.profileRelation}>{person.relationship}</div>
          <div style={styles.profileTagRow}>
            <span
              style={{ ...styles.profileTag, background: cat?.color || "#555" }}
            >
              {cat?.icon} {cat?.label}
            </span>
            {person.intimatePartner && (
              <span style={{ ...styles.profileTag, background: "#7B68AE" }}>
                💜 Intimate Partner
              </span>
            )}
            {person.phoneNumberGiven && (
              <span style={{ ...styles.profileTag, background: "#333" }}>
                {person.phoneNumberGiven === "primary_a"
                  ? "Primary (A)"
                  : "Secondary (M)"}
              </span>
            )}
          </div>
        </div>
        <div style={styles.profileActions}>
          <button onClick={() => onEdit(person)} style={styles.profileEditBtn}>
            Edit
          </button>
          <button
            onClick={() => {
              if (confirm(`Remove ${person.name}?`)) onDelete(person.id);
            }}
            style={styles.profileDeleteBtn}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Content Grid */}
      <div style={styles.profileGrid}>
        {/* Left Column - Details */}
        <div style={styles.profileDetailsCol}>
          <div style={styles.profileSection}>
            <h3 style={styles.profileSectionTitle}>Details</h3>
            <div style={styles.profileDetailsList}>
              {person.jobDescription && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>💼</span>
                  <div>
                    <div style={styles.profileDetailLabel}>Job Description</div>
                    <div style={styles.profileDetailValue}>
                      {person.jobDescription}
                    </div>
                  </div>
                </div>
              )}
              {person.phoneNumber && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>📞</span>
                  <div>
                    <div style={styles.profileDetailLabel}>Phone Number</div>
                    <div style={styles.profileDetailValue}>
                      {person.phoneNumber}
                    </div>
                  </div>
                </div>
              )}
              {person.meetingPlace && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>📍</span>
                  <div>
                    <div style={styles.profileDetailLabel}>Meeting Place</div>
                    <div style={styles.profileDetailValue}>
                      {person.meetingPlace}
                    </div>
                  </div>
                </div>
              )}
              {person.significantOther && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>💕</span>
                  <div>
                    <div style={styles.profileDetailLabel}>
                      Significant Other
                    </div>
                    <div style={styles.profileDetailValue}>
                      {person.significantOther}
                    </div>
                  </div>
                </div>
              )}
              {person.dateMet && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>🤝</span>
                  <div>
                    <div style={styles.profileDetailLabel}>Date Met</div>
                    <div style={styles.profileDetailValue}>
                      {formatDate(person.dateMet)}
                      {daysSinceMet !== null
                        ? ` (${daysSinceMet} days ago)`
                        : ""}
                    </div>
                  </div>
                </div>
              )}
              {person.birthdate && (
                <div style={styles.profileDetailItem}>
                  <span style={styles.profileDetailIcon}>🎂</span>
                  <div>
                    <div style={styles.profileDetailLabel}>Birthdate</div>
                    <div style={styles.profileDetailValue}>
                      {formatDate(person.birthdate)}
                      {age !== null ? ` (Age ${age})` : ""}
                    </div>
                    {nextBirthday && (
                      <div style={styles.profileDetailSub}>
                        Next birthday: {nextBirthday}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {person.intimatePartner &&
                (person.intimateStart || person.intimateEnd) && (
                  <div style={styles.profileDetailItem}>
                    <span style={styles.profileDetailIcon}>💜</span>
                    <div>
                      <div style={styles.profileDetailLabel}>
                        Intimate Period
                      </div>
                      <div style={styles.profileDetailValue}>
                        {person.intimateStart
                          ? new Date(
                              person.intimateStart + "T00:00:00",
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              year: "numeric",
                            })
                          : "?"}{" "}
                        –{" "}
                        {person.intimateEnd
                          ? new Date(
                              person.intimateEnd + "T00:00:00",
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              year: "numeric",
                            })
                          : "Present"}
                      </div>
                    </div>
                  </div>
                )}
              <div style={styles.profileDetailItem}>
                <span style={styles.profileDetailIcon}>📅</span>
                <div>
                  <div style={styles.profileDetailLabel}>Added to Journal</div>
                  <div style={styles.profileDetailValue}>
                    {new Date(person.dateAdded).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {person.notes && (
            <div style={styles.profileSection}>
              <h3 style={styles.profileSectionTitle}>Notes</h3>
              <div style={styles.profileNotes}>{person.notes}</div>
            </div>
          )}
        </div>

        {/* Right Column - Connections & Nights */}
        <div style={styles.profileSideCol}>
          {/* Connections */}
          {(parent || children.length > 0) && (
            <div style={styles.profileSection}>
              <h3 style={styles.profileSectionTitle}>Connections</h3>
              {parent && (
                <div
                  style={styles.profileConnectionItem}
                  onClick={() => onSelectPerson(parent)}
                >
                  <ProfileAvatar
                    person={parent}
                    size={32}
                    fontSize={13}
                    color={CATEGORY_MAP[parent.category]?.color}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.profileConnectionName}>
                      {parent.name}
                    </div>
                    <div style={styles.profileConnectionRole}>Linked above</div>
                  </div>
                  <span style={{ color: "#555", fontSize: 14 }}>→</span>
                </div>
              )}
              {children.map((c) => (
                <div
                  key={c.id}
                  style={styles.profileConnectionItem}
                  onClick={() => onSelectPerson(c)}
                >
                  <ProfileAvatar
                    person={c}
                    size={32}
                    fontSize={13}
                    color={CATEGORY_MAP[c.category]?.color}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.profileConnectionName}>{c.name}</div>
                    <div style={styles.profileConnectionRole}>
                      {c.relationship}
                    </div>
                  </div>
                  <span style={{ color: "#555", fontSize: 14 }}>→</span>
                </div>
              ))}
            </div>
          )}

          {/* Nights Together */}
          {person.nightsTogether && person.nightsTogether.length > 0 && (
            <div style={styles.profileSection}>
              <h3 style={styles.profileSectionTitle}>
                Nights Together ({person.nightsTogether.length})
              </h3>
              <div style={styles.profileNightsList}>
                {[...person.nightsTogether]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((n) => (
                    <div key={n.id} style={styles.profileNightsItem}>
                      <span style={styles.profileNightsDate}>
                        {new Date(n.date + "T00:00:00").toLocaleDateString(
                          "en-US",
                          { month: "short", day: "numeric", year: "numeric" },
                        )}
                      </span>
                      {n.label && (
                        <span style={styles.profileNightsLabel}>{n.label}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div style={styles.profileSection}>
            <h3 style={styles.profileSectionTitle}>Quick Stats</h3>
            <div style={styles.profileQuickStats}>
              {daysSinceMet !== null && (
                <div style={styles.profileQuickStat}>
                  <div style={styles.profileQuickStatValue}>{daysSinceMet}</div>
                  <div style={styles.profileQuickStatLabel}>Days Known</div>
                </div>
              )}
              {children.length > 0 && (
                <div style={styles.profileQuickStat}>
                  <div style={styles.profileQuickStatValue}>
                    {children.length}
                  </div>
                  <div style={styles.profileQuickStatLabel}>Linked Below</div>
                </div>
              )}
              {person.nightsTogether && person.nightsTogether.length > 0 && (
                <div style={styles.profileQuickStat}>
                  <div style={styles.profileQuickStatValue}>
                    {person.nightsTogether.length}
                  </div>
                  <div style={styles.profileQuickStatLabel}>Nights</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── BACKUP & RESTORE VIEW ───────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
